// todo: move out of messages for global access

// Styling system uses react-emotion - a javascript styling system based on styled components
// and influenced by other libraries: https://github.com/emotion-js/emotion | https://emotion.sh/
// Blog post: https://medium.com/@tkh44/emotion-ad1c45c6d28b
// Core idea: create component style containers using template literals
// Advantages: semantic markup, use js and react props in component styles to
// visually adapt to current state + lots of ways to combine/compose styles.
// Going forward: build style guide for anyroad from small
// visual components instead of fragile css.
import { css } from '@emotion/react';
import styled from '@emotion/styled';
// Theme is a js object containing global values such as colors, widths.
import { Theme, statusColor } from 'ui_system/theme';

const { isIE } = window.mobileDetect;
const { isEdge } = window.mobileDetect;
const isMobile = !!window.mobileDetect.mobile();

export { Theme, statusColor, isIE, isMobile, isEdge };

// CSS STYLES

const cssSelectedBackground = css`
  background-color: ${Theme.colors.backgroundColor};
`;

export const cssRemoveInputFocus = css`
  &:focus {
    outline: none;
  }
`;

export const cssOverflowEllipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const cssWordBreak = css`
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

// Support for IE11 specific grid reqs
export const cssIEGridSupport = !isIE
  ? css``
  : css`
      & > div:nth-child(1) {
        -ms-grid-column: 1;
      }
      & > div:nth-child(2) {
        -ms-grid-column: 2;
      }
      & > div:nth-child(3) {
        -ms-grid-column: 3;
      }
    `;

export const cssListItem = css`
  border-bottom: solid 1px ${Theme.colors.dividerInactionableFieldColor};
  cursor: pointer;
  &:hover {
    ${cssSelectedBackground}
  }
`;

export const cssIEOverflowY = css`
  -ms-overflow-y: scroll;
`;

export const cssActiveListItem = css`
  border-left: 8px;
  border-right: 0px;
  border-style: solid;
  border-image: ${Theme.colors.anygradient};
  ${cssSelectedBackground}
`;

export const cssHoverable = css`
  &:hover {
    filter: brightness(110%) saturate(125%);
  }
  &:disabled {
    filter: none;
    opacity: 0.3;
  }
`;

export const cssNoSpin = css`
  -webkit-appearance: none;
  margin: 0;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
// STYLED COMPONENTS

export const TruncatedGradient = styled.div`
  content: '';
  position: absolute;
  width: 100%;
  height: ${(props) => (props.height ? `${props.height}px` : '50px')};
  left: 0;
  bottom: ${(props) => (props.bottom ? `${props.bottom}px` : '0')};
  background: -moz-linear-gradient(
    top,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 100%
  ); /* FF3.6+ */
  background: -webkit-gradient(
    linear,
    left top,
    left bottom,
    color-stop(50%, rgba(255, 255, 255, 0)),
    color-stop(100%, rgba(255, 255, 255, 1))
  ); /* Chrome,Safari4+ */
  background: -webkit-linear-gradient(
    top,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 100%
  ); /* Chrome10+,Safari5.1+ */
  background: -o-linear-gradient(
    top,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 100%
  ); /* Opera 11.10+ */
  background: -ms-linear-gradient(
    top,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 100%
  ); /* IE10+ */
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 100%
  ); /* W3C */
  filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#00000000', endColorstr='#000000',GradientType=0 ); /* IE6-9 */
`;

export const Cap = styled.span`
  text-transform: capitalize;
`;

export const BookingStatus = styled.div`
  color: ${(props) => statusColor(props.status)};
  text-transform: uppercase;
`;

export const Spacer = styled.div`
  display: inline-block;
  width: ${(props) => (props.width ? `${props.width}px` : '10px')};
`;

export const Pill = styled.div`
  display: inline-block;
  background-color: #cecece;
  color: white;
  font-size: 11px;
  border-radius: 6px;
  padding: 2px 6px;
  width: ${(props) => (props.width ? `${props.width}px` : 'auto')};
`;

export const GreenDot = styled.div`
  color: ${Theme.colors.linksDesignElementsBaseColor};
  font-size: 38px;
  line-height: 30%;
  margin-left: 1px;
`;

export const SubtleText = styled.div`
  color: ${Theme.colors.secondaryTextColor};
  font-size: 12px;
`;

export const UserName = styled.div`
  color: ${Theme.colors.primaryTextColor};
  font-size: 16px;
  font-weight: ${(props) => (props.bold ? '600' : 'normal')};
  line-height: 20px;
  ${cssOverflowEllipsis}
`;

export const Email = styled.span`
  color: ${Theme.colors.secondaryTextColor};
`;

export const TourName = styled.div`
  color: ${Theme.colors.secondaryTextColor};
  font-size: 14px;
  font-weight: ${(props) => (props.bold ? 'bold' : 'normal')};
  ${cssOverflowEllipsis}
  a {
    color: ${Theme.colors.secondaryTextColor};
  }
`;

export const Time = styled.div`
  color: ${Theme.colors.secondaryTextColor};
  font-size: 12px;
  text-align: right;
`;

export const FadeIn = styled.div`
  opacity: ${(props) => (props.loading ? 0 : 1)};
  transition: opacity ${(props) => (props.ms ? `${props.ms}ms` : '300ms')}
    linear;
`;

export const CircleAvatar = styled.div`
  background-color: ${Theme.colors.darkText};
  border-radius: 50%;
  color: #fff;
  font-size: ${(props) =>
    props.size ? `${parseInt(props.size, 10) / 3}px` : '14px'};
  font-weight: lighter;
  height: ${(props) => (props.size ? `${props.size}px` : '32px')} !important;
  line-height: ${(props) => (props.size ? `${props.size}px` : '32px')};
  overflow: hidden;
  position: relative;
  text-align: center;
  top: ${(props) =>
    props.size ? `-${parseInt(props.size, 10) / 4}px` : '32px'};
  width: ${(props) => (props.size ? `${props.size}px` : '32px')} !important;
  img {
    width: ${(props) => (props.size ? `${props.size}px` : '32px')};
    height: ${(props) => (props.size ? `${props.size}px` : '32px')};
    vertical-align: top;
  }
`;

export const Button = styled.button`
  background-color: ${(props) => (props.bgColor ? props.bgColor : '#fff')};
  border-radius: 4px;
  color: ${(props) =>
    props.color ? props.color : Theme.colors.secondaryTextColor};
  font-size: 14px;
  padding: 10px 0;
  width: 120px;
  i {
    margin-right: 10px;
  }
  @media (max-width: ${Theme.mobile.phoneMaxWidth}px) {
    font-size: 13px;
    padding: 8px 24px;
  }
  ${cssRemoveInputFocus}
  ${(props) => (props.hoverable ? cssHoverable : '')};
`;

export const Grid = styled.div`
  ${isIE && 'display: -ms-grid;'}
  display: grid;
  -ms-grid-columns: ${(props) => (isIE ? props.gridTemplateColumns : '')};
  grid-template-columns: ${(props) => props.gridTemplateColumns};
  -ms-grid-rows: ${(props) => (isIE ? props.gridTemplateRows : '')};
  grid-template-rows: ${(props) => props.gridTemplateRows};
  ${cssIEGridSupport}
  ${(props) => props.alignItems && `align-items: ${props.alignItems};`}
  ${(props) => props.justifyItems && `justify-items: ${props.justifyItems};`}
  grid-gap: ${(props) => (props.gridGap ? props.gridGap : '0')};
  @media (max-width: ${Theme.mobile.phoneMaxWidth}px) {
    ${(props) =>
      props.responsiveTemplateColumns
        ? `grid-template-columns: ${props.responsiveTemplateColumns}`
        : ''};
    ${(props) =>
      props.responsiveTemplateRows
        ? `grid-template-rows: ${props.responsiveTemplateRows}`
        : ''};
  }
`;

export const DashHeader = styled.h2`
  font-family: 'soleil';
  font-size: 16px;
  margin-top: 0;
`;

export const MoreLink = styled.a`
  cursor: pointer;
  color: ${Theme.colors.linksDesignElementsBaseColor};
  display: block;
  &:hover {
    color: ${Theme.colors.linksDesignElementsBaseColor};
  }
`;

export const HeaderButton = styled.button`
  background: none;
  border: none;
  border-radius: 0;
  color: ${Theme.colors.linksDesignElementsBaseColor};
  cursor: pointer;
  font-weight: 400;
  ${(props) => (!props.noBorder ? 'margin-right: 0' : 'margin-right: 0px')};
  ${(props) =>
    !props.noBorder ? 'padding: 2px 18px 0px' : 'padding: 0 0 4px 18px'};
  ${(props) =>
    !props.noBorder &&
    `border-right: solid 1px ${Theme.colors.dividerInactionableFieldColor}`};
  line-height: 15px;
  text-align: center;
  svg {
    margin-right: 8px;
    vertical-align: middle;
  }
  ${cssRemoveInputFocus}
  @media (max-width: ${Theme.mobile.phoneMaxWidth}px) {
    margin: 0;
    padding: 0;
    svg {
      margin-right: 5px;
    }
  }
`;

export const HR = styled.hr`
  border: none;
  background: ${Theme.colors.dividerInactionableFieldColor};
  height: 1px;
  outline: none;
  margin: 10px 0;
`;
